import React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import WarningIcon from "@mui/icons-material/Warning";
import { PromptInfo, Step } from "./types";
import { Loader } from "./Loader";
import { useAppContext } from "../contexts/AppContext";
import { Box, useTheme } from "@mui/material";

interface PromptProps {
  promptInfo: PromptInfo;
  onSwitchStep: (step: Step) => void;
  // onLogin function basically
  onOkAction?: () => void;
  onSecondaryAction?: () => void;
}

const Prompt: React.FC<PromptProps> = ({
  promptInfo,
  onSwitchStep,
  onOkAction,
  onSecondaryAction,
}) => {
  const theme = useTheme();
  const errorColor = theme.palette.error.main;

  const { loading } = useAppContext();

  const onOkClickHandler = () => {
    if (onOkAction) {
      onOkAction();
      return;
    }
    onSwitchStep(
      promptInfo.nextStep === Step.VERIFICATION_CODE
        ? Step.SEND_VERIFICATION_CODE
        : promptInfo.nextStep
    );
  };

  return (
    <Container>
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={8} md={8}>
          <Card>
            <CardContent>
              <Box style={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  variant="h6"
                  align="center"
                  style={{ position: "relative", display: "inline" }}
                >
                  <WarningIcon
                    sx={{ fontSize: 20 }}
                    style={{
                      top: "50%",
                      position: "absolute",
                      left: "-2rem",
                      transform: "translate(0, -50%)",
                    }}
                  />
                  {promptInfo.title}
                </Typography>
              </Box>
              <div style={{ textAlign: "center" }}>
                <p
                  style={{ color: errorColor }}
                  dangerouslySetInnerHTML={{ __html: promptInfo.errorMessage }}
                ></p>
                <p dangerouslySetInnerHTML={{ __html: promptInfo.message }}></p>
              </div>

              {promptInfo.nextVisit && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={1}
                  padding={2}
                  borderRadius={1}
                  sx={{ bgcolor: "#e6f1ff" }}
                >
                  <Typography fontWeight="bold" align="center">
                    {promptInfo.nextVisit.title}
                  </Typography>
                  <Typography align="center">
                    {formatVisitDate(
                      promptInfo.nextVisit.serviceDate,
                      promptInfo.nextVisit.serviceTime
                    )}
                  </Typography>
                </Box>
              )}

              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={onOkClickHandler}
                fullWidth
                style={{ marginTop: "1rem" }}
                disabled={loading}
              >
                {promptInfo.okLabel ?? "OK"} {loading && <Loader />}
              </Button>

              {promptInfo.secondaryLabel && onSecondaryAction && (
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  onClick={onSecondaryAction}
                  fullWidth
                  style={{ marginTop: "0.75rem" }}
                  disabled={loading}
                >
                  {promptInfo.secondaryLabel}
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Dates arrive as plain YYYY-MM-DD and times as HH:MM:SS, both already in the
// clinic's timezone, so they are read apart rather than through Date parsing.
export const formatVisitDate = (
  serviceDate: string,
  serviceTime?: string
): string => {
  const [year, month, day] = serviceDate.split("-").map(Number);
  if (!year || !month || !day) return serviceDate;

  const weekday = WEEKDAYS[new Date(year, month - 1, day).getDay()];
  const formatted = `${weekday}, ${MONTHS[month - 1]} ${day} ${year}`;

  const time = formatVisitTime(serviceTime);

  return time ? `${formatted} \u00b7 ${time}` : formatted;
};

const formatVisitTime = (serviceTime?: string): string => {
  if (!serviceTime) return "";

  const [hours, minutes] = serviceTime.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return "";

  const suffix = hours < 12 ? "AM" : "PM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;

  return `${hour12}:${String(minutes).padStart(2, "0")} ${suffix}`;
};

export default Prompt;
